import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";
import {
  createTemplate,
  fetchTemplate,
  fetchTemplateCategories,
  updateTemplate,
} from "../../../api/templates";
import toast from "react-hot-toast";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { axiosWithToast } from "../../../components/Utils";
import { PMField } from "../../../components/utils/PMField";
import { FormikSaveBar } from "../../../components/utils/FormikSaveBar";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { PromangRichTextEditor } from "../../../components/utils/PromangRichTextEditor";

export const TemplatePage = () => {
  const { t } = useTranslation();

  const { id } = useParams();

  const [isLoading, setIsLoading] = useState(true);

  const [post, setPost] = useState(null);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    // if ID is set, fetch the post
    setTimeout(() => {
      if (id) {
        setIsLoading(true);
        getFaqPost(id);
      } else {
        setIsLoading(false);
      }
    }, 500);
  }, [id]);

  useEffect(() => {
    fetchTemplateCategories({ tree: true }).then((res) => {
      setCategories(res?.data?.data || []);
    });
  }, []);

  const getFaqPost = (id) => {
    setIsLoading(true);
    try {
      fetchTemplate(id).then((data) => {
        setIsLoading(false);
        setPost(data?.data);
      });
    } catch {
      toast.error("Something went wrong when trying to load the report");

      setIsLoading(false);
    }
  };

  const headerActions = [
    // {
    //   component: null,
    //   label: "Ny innlegg",
    //   description: "",
    //   path: null,
    //   action: () => toast.success("Hej Robin"),
    //   icon: PlusIcon,
    //   textColor: "text-teal-700",
    //   bgColor: "bg-white",
    //   hidden: false,
    //   menu: true,
    // },
  ];

  const createOrUpdateDispatch = (data) => {
    const payload = {
      ...data,
      template_category_id: data.template_category_id || null,
    };
    // onEditing(false);
    if (post?.id) {
      const res = updateTemplate(payload);
      return res;
    } else {
      const res = createTemplate(payload);
      return res;
    }
  };

  function Content() {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
            <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <Link
                  to="/dashboard/templates"
                  className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-slate-50"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  {t("Back")}
                </Link>
                <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                  <DocumentTextIcon className="h-4 w-4" />
                  {post?.id ? t("templates.edit") : t("templates.new")}
                </div>
                <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                  {post?.title || t("templates.new")}
                </h1>
                <p className="mt-2 max-w-2xl text-sm text-slate-600">
                  {t("templates.editor_hint")}
                </p>
              </div>
              <div className="rounded-2xl bg-slate-100 px-4 py-3 text-sm text-slate-700">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                  {t("templates.categories.title")}
                </div>
                <div className="mt-1 font-semibold text-slate-900">
                  {post?.category?.name || t("templates.categories.uncategorized")}
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm">
            <Formik
              initialValues={{
                id: post?.id,
                title: post?.title || "",
                content: post?.content || "",
                template_category_id:
                  post?.template_category_id || post?.category?.id || "",
                public: post?.public || false,
                submit: null,
              }}
              validationSchema={Yup.object({
                title: Yup.string().max(255).required(t("Title is Required")),
                description: Yup.string(),
                // category_id: Yup.number().required(t("Category is required")),
              })}
              onSubmit={(values, helpers) => {
                try {
                  axiosWithToast(createOrUpdateDispatch(values), (data) => {
                    setPost(data);
                  });
                  helpers.setStatus({ success: true });
                  helpers.setSubmitting(false);
                } catch (err) {
                  toast.error("Nope");
                  console.error(err);
                  helpers.setStatus({ success: false });
                  helpers.setErrors({ submit: err.message });
                  helpers.setSubmitting(false);
                }
              }}
            >
              {(formik) => (
                <Form>
                  <div className="border-b border-slate-200 px-6 py-6">
                    <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
                      <FolderIcon className="h-4 w-4" />
                      {t("templates.section.kicker")}
                    </div>
                    <h2 className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">
                      {t("templates.form.title")}
                    </h2>
                    <p className="mt-1 text-sm text-slate-600">
                      {t("templates.form.subtitle")}
                    </p>
                  </div>

                  <div className="space-y-8 px-6 py-6">
                    <div className="grid gap-6 lg:grid-cols-3">
                      <div className="lg:col-span-2">
                        <PMField
                          name="title"
                          type="text"
                          label={t("Title")}
                          {...formik}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          {t("templates.categories.title")}
                        </label>
                        <select
                          name="template_category_id"
                          value={formik.values.template_category_id || ""}
                          onChange={formik.handleChange}
                          className="mt-2 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        >
                          <option value="">
                            {t("templates.categories.uncategorized")}
                          </option>
                          {flattenCategories(categories).map((category) => (
                            <option key={category.value} value={category.value}>
                              {category.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700">
                        {t("Content")}
                      </label>
                      <PromangRichTextEditor
                        value={formik.values.content}
                        onChange={(value) => formik.setFieldValue("content", value)}
                        placeholder={t("templates.editor_placeholder")}
                        size="lg"
                      />
                    </div>
                  </div>
                  <FormikSaveBar {...formik} />
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <DashboardHeader
        subtitle=""
        actions={headerActions}
        title={post?.id ? t("templates.edit") : t("templates.new")}
      >
        {isLoading ? <ResourceLoading /> : <Content />}
      </DashboardHeader>
    </>
  );
};

function flattenCategories(categories = [], level = 0) {
  return categories.flatMap((category) => [
    {
      value: category.id,
      label: `${"— ".repeat(level)}${category.name}`,
    },
    ...flattenCategories(category.children || [], level + 1),
  ]);
}
